<template>
  <InputWrapper
    v-slot="{ inputId }"
    :input-id="id"
    :label="refNumberType.referenceNumberType?.name"
    class="reference-number-filter"
    optional
  >
    <RygenDropdown
      v-if="isDropdown"
      :id="inputId"
      v-model="internalDropdownReferenceNumber"
      append-to="body"
      :options="refNumberType.referenceNumberType?.valueOptions"
      :placeholder="refNumberType.referenceNumberType?.name"
      option-label="label"
      option-value="value"
      show-clear
    />
    <RygenMultiSelect
      v-else-if="isMultiChoice"
      :id="inputId"
      v-model="internalReferenceNumbers"
      append-to="body"
      :options="refNumberType.referenceNumberType?.valueOptions"
      :placeholder="refNumberType.referenceNumberType?.name"
      option-label="label"
      option-value="value"
      filter
    />
    <RygenCalendar
      v-else-if="isDateOrTimeType"
      :id="inputId"
      v-model="internalReferenceNumberAsDate"
      :placeholder="refNumberType.referenceNumberType?.name"
      :show-time="isTimeType"
      :time-only="isTimeTypeOnly"
      hour-format="12"
    />

    <!--
      Supports multiple values and copy/paste from excel, csv, etc.
      Used for `TEXT` and `NUMERIC` reference numbers
    -->
    <GenericTermFilter
      v-else
      :id="inputId"
      v-model="internalReferenceNumbers"
      :placeholder="refNumberType.referenceNumberType?.name"
    />
  </InputWrapper>
</template>

<script lang="ts" setup>
import { DateTime } from 'luxon';
import { computed, PropType, toRefs, WritableComputedRef } from 'vue';

import { useModelValue } from '@rygen/rygen-ui/composables';
import { ClientReferenceNumberType } from '@rygen/rygen-ui/model';

import { useReferenceNumberValueTypes } from '@/composables';
import {
  OrderSearchCriteria,
  ReferenceNumber,
  ShipmentSearchCriteria,
} from '@/model';

import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenCalendar from '@rygen/rygen-ui/components/form/RygenCalendar.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import RygenMultiSelect from '@rygen/rygen-ui/components/form/RygenMultiSelect.vue';
import GenericTermFilter from '@rygen/rygen-ui/components/search/GenericTermFilter.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Object as PropType<OrderSearchCriteria | ShipmentSearchCriteria>,
    default: () => new OrderSearchCriteria(),
  },
  refNumberType: {
    type: ClientReferenceNumberType,
    required: true,
  },
});
const { id, modelValue, refNumberType } = toRefs(props);
const emit = defineEmits<{
  (
    event: 'update:model-value',
    newVal: OrderSearchCriteria | ShipmentSearchCriteria,
  ): void;
}>();

const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (
    opts: Partial<OrderSearchCriteria | ShipmentSearchCriteria>,
  ) => {
    if (OrderSearchCriteria.isOrderSearchCriteria(opts)) {
      return new OrderSearchCriteria(opts);
    } else if (ShipmentSearchCriteria.isShipmentSearchCriteria(opts)) {
      return new ShipmentSearchCriteria(opts);
    } else {
      return new OrderSearchCriteria();
    }
  },
});

const {
  isDateOrTimeType,
  isMultiChoice,
  isTimeType,
  isTimeTypeOnly,
  isDropdown,
} = useReferenceNumberValueTypes(refNumberType);

const internalDropdownReferenceNumber: WritableComputedRef<
  string | undefined,
  string | undefined
> = computed({
  get: () => {
    return internalValue.value.referenceNumbers
      .filter(
        (ref) =>
          ref.value &&
          ref.typeNaturalId ===
            refNumberType.value.referenceNumberType?.naturalId,
      )
      .map((ref) => ref.value!) // Filter above already removes undefined
      .at(0);
  },
  set: (newVal: string | undefined) => {
    if (!newVal) {
      internalValue.value.referenceNumbers = [];
      return;
    }

    const newReferenceNumber = new ReferenceNumber({
      value: newVal,
      typeNaturalId: refNumberType.value.referenceNumberType?.naturalId,
      levels: refNumberType.value.levels,
    });

    const filteredReferences = internalValue.value.referenceNumbers.filter(
      (val) =>
        val.typeNaturalId !==
        refNumberType.value.referenceNumberType?.naturalId,
    );

    internalValue.value.referenceNumbers = [
      ...filteredReferences,
      newReferenceNumber,
    ];
  },
});

const internalReferenceNumberAsDate: WritableComputedRef<
  DateTime<boolean> | undefined,
  DateTime<boolean> | undefined
> = computed({
  get: () => {
    return internalValue.value.referenceNumbers
      .filter(
        (ref) =>
          ref.value &&
          ref.typeNaturalId ===
            refNumberType.value.referenceNumberType?.naturalId,
      )
      .map((ref) => ref.valueAsDateTime)
      .at(0);
  },
  set: (newVal: DateTime<boolean> | undefined) => {
    if (!newVal) {
      internalValue.value.referenceNumbers = [];
      return;
    }

    let searchValue: string | undefined;
    if (isTimeTypeOnly.value) {
      searchValue = newVal.toFormat('HH:mm:ss') ?? undefined;
    } else if (isTimeType.value) {
      searchValue = newVal.toISO({ includeOffset: false }) ?? undefined;
    } else {
      searchValue = newVal.toISODate() ?? undefined;
    }

    const newReferenceNumber = new ReferenceNumber({
      value: searchValue,
      typeNaturalId: refNumberType.value.referenceNumberType?.naturalId,
      levels: refNumberType.value.levels,
    });

    const filteredReferences = internalValue.value.referenceNumbers.filter(
      (val) =>
        val.typeNaturalId !==
        refNumberType.value.referenceNumberType?.naturalId,
    );

    internalValue.value.referenceNumbers = [
      ...filteredReferences,
      newReferenceNumber,
    ];
  },
});

const internalReferenceNumbers: WritableComputedRef<string[], string[]> =
  computed({
    get: () => {
      return internalValue.value.referenceNumbers
        .filter(
          (ref) =>
            ref.value &&
            ref.typeNaturalId ===
              refNumberType.value.referenceNumberType?.naturalId,
        )
        .map((ref) => ref.value!); // Filter above already removes undefined
    },
    set: (newVals: string[]) => {
      const newReferenceNumbers = newVals.map(
        (newVal) =>
          new ReferenceNumber({
            value: newVal,
            typeNaturalId: refNumberType.value.referenceNumberType?.naturalId,
            levels: refNumberType.value.levels,
          }),
      );

      const filteredReferences = internalValue.value.referenceNumbers.filter(
        (val) =>
          val.typeNaturalId !==
          refNumberType.value.referenceNumberType?.naturalId,
      );

      internalValue.value.referenceNumbers = [
        ...filteredReferences,
        ...newReferenceNumbers,
      ];
    },
  });
</script>
